// Orchestrator for the meeting "Main area" layout.
//
//   FEED  : no screen share and no pinned feeds -> a grid of participant feeds.
//   SPLIT : a screen is shared OR feeds are pinned -> Stage + Feed strip.
//           Screen share wins the Stage; pinned feeds (if any) demote to the
//           strip. With no screen share, pinned feeds form the Stage.
//
// Input `container` must be the measured Main-area box. All output rects are
// relative to it. Events must be passed pre-sorted by the caller; the engine
// itself is order-agnostic (it only counts feeds).

import { computeFeedGrid, placeFeedGrid, planFeedGrid } from './feed-grid';
import { computePinnedStageProbe } from './pinned-stage';
import { computeSplitLayout } from './split-layout';
import {
    DEFAULT_GAP,
    LayoutInput,
    LayoutResult,
    MIN_FEED_WIDTH,
} from './types';

export const computeMeetingLayout = (input: LayoutInput): LayoutResult => {
    const gap = input.gap ?? DEFAULT_GAP;
    const { container, feedCount } = input;
    const pinnedCount = input.pinnedCount ?? 0;
    const screenAspect = input.screenAspect ?? null;
    const minFeedWidth = input.minFeedWidth ?? MIN_FEED_WIDTH;
    const maxVisible = input.maxVisibleFeeds;
    // `feedOnly` forces a plain FEED grid (the dock sets it when nothing claims
    // the stage); otherwise a screen share or pinned feeds drive a SPLIT — in
    // the dock too, where the narrow pane resolves to a vertical stage + strip.
    const layoutIsSplit =
        !input.feedOnly && (screenAspect != null || pinnedCount > 0);

    if (!layoutIsSplit) {
        const grid = computeFeedGrid(
            { x: 0, y: 0, width: container.width, height: container.height },
            feedCount,
            { gap, minWidth: minFeedWidth, maxVisible },
        );
        return {
            mode: 'FEED',
            feedTiles: grid.tiles,
            feedTileAspect: grid.tileAspect,
            visibleFeedCount: grid.visibleCount,
            overflowCount: grid.overflowCount,
        };
    }

    // Screen share wins the Stage; pinned feeds (if any) fall back to the strip.
    if (screenAspect != null) {
        const split = computeSplitLayout(container, screenAspect, feedCount, {
            gap,
            maxVisible,
            minWidth: minFeedWidth,
        });
        return {
            mode: 'SPLIT',
            orientation: split.orientation,
            stage: split.stage,
            feedArea: split.feedArea,
            feedTiles: split.feedGrid.tiles,
            feedTileAspect: split.feedGrid.tileAspect,
            visibleFeedCount: split.feedGrid.visibleCount,
            overflowCount: split.feedGrid.overflowCount,
        };
    }

    // Pinned-driven Stage (no screen share). Probe the pinned grid's aspect
    // against the full container to pick orientation, then re-fit it into the
    // committed stage band.
    const probe = computePinnedStageProbe(container, pinnedCount, gap);
    const split = computeSplitLayout(container, probe.aspect, feedCount, {
        gap,
        maxVisible,
        minWidth: minFeedWidth,
    });
    const pinnedPlan = planFeedGrid(
        { width: split.stage.width, height: split.stage.height },
        pinnedCount,
        { minWidth: 0, gap },
    );
    return {
        mode: 'SPLIT',
        orientation: split.orientation,
        stage: split.stage,
        pinnedTiles: placeFeedGrid(split.stage, pinnedPlan, gap),
        feedArea: split.feedArea,
        feedTiles: split.feedGrid.tiles,
        feedTileAspect: split.feedGrid.tileAspect,
        visibleFeedCount: split.feedGrid.visibleCount,
        overflowCount: split.feedGrid.overflowCount,
    };
};

export * from './types';
export { computeFeedGrid, planFeedGrid, placeFeedGrid } from './feed-grid';
export { computeSplitLayout } from './split-layout';
export { computePinnedStageProbe } from './pinned-stage';
